<script setup>
import { ref, reactive, computed, nextTick, onMounted, onActivated } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()
const { obterBadgeNeutro } = useUI()

const listas = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(true)

// Estado para o Modal de Auditoria
const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  nomeLista: ''
})

// ============================================================
// Toolbar completa do AdminTable — estado da exportação
// ============================================================
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const filtrosExportacao = computed(() =>
  filtros.nomeLista ? `Nome: "${filtros.nomeLista}"` : 'Nenhum filtro aplicado'
)

const colunas = [
  { field: 'idListaSistemaCabecalho', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nomeLista', label: 'Nome da Lista', sortable: true, class: 'fw-medium' },
  { field: 'descricaoLista', label: 'Descrição', sortable: true },
  { field: 'prefixoItemLista', label: 'Prefixo', width: 200, sortable: true, slotName: 'prefixo' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

// Colunas disponíveis para o gerenciador de layouts da toolbar (sem a coluna de ações)
const colunasPadrao = [
  { field: 'idListaSistemaCabecalho', label: 'ID', width: 80, sortable: true },
  { field: 'nomeLista', label: 'Nome da Lista', sortable: true },
  { field: 'descricaoLista', label: 'Descrição', sortable: true },
  { field: 'prefixoItemLista', label: 'Prefixo', width: 200, sortable: true }
]

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (filtros.nomeLista) params.append('busca', filtros.nomeLista)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/ListasSistema?${params.toString()}`)

    listas.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar listas do sistema:', error)
    erro.value = 'Não foi possível carregar os dados das listas.'
    if (error.response && error.response.status === 403) {
      erro.value = 'Acesso negado. Apenas Administradores Internos podem ver este módulo.'
    }
    listas.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.nomeLista = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarLista = () => {
  emit('adicionar-item')
}

const editarLista = (lista) => {
  emit('editar-item', lista)
}

const abrirAuditoria = (lista) => {
  auditoriaSelecionada.id = lista.idListaSistemaCabecalho
  auditoriaSelecionada.nome = lista.nomeLista
  mostrarAuditoria.value = true
}

// ============================================================
// Exportação (toolbar do AdminTable) — percorre todas as páginas
// ============================================================
async function handleExportarDados () {
  const totalRegistros = paginacao.value.TotalCount || 0
  if (!totalRegistros) {
    emit('show-notification', { message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)
      if (filtros.nomeLista) params.append('busca', filtros.nomeLista)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)

      const response = await api.get(`/ListasSistema?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]

    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[ListasSistemaAdministrar] Erro na exportação:', e)
    emit('show-notification', { message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

onMounted(() => {
  buscarDados(1)
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Listas do Sistema"
    subtitulo="Gestão de listas internas da plataforma"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarLista"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="true"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label for="filtro-nome" class="form-label">Nome da Lista</label>
          <input type="text" id="filtro-nome" class="form-control" v-model="filtros.nomeLista" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>

        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="listas"
      :loading="loading"
      empty-message="Nenhum registo encontrado."
      row-key="idListaSistemaCabecalho"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarLista($event.row)"
      modulo-layout="ListasSistema"
      :colunas-padrao="colunasPadrao"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Listas do Sistema"
      :filtros-exportacao="filtrosExportacao"
      @exportar-dados="handleExportarDados"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idListaSistemaCabecalho }}</span>
      </template>
      <template #prefixo="{ row }">
        <span :class="obterBadgeNeutro()">{{ row.prefixoItemLista }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" title="Editar" @click="editarLista(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <!-- <button class="btn-icon" title="Excluir" @click="excluirLista(row)">
            <i class="bi bi-trash-fill text-danger"></i>
          </button> -->
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ListaSistemaCabecalho"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="master"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
